import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PageHead from "@/components/PageHead";
import { Mail } from "lucide-react";

export default function NewsletterUnsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Missing unsubscribe token.");
      return;
    }
    let mounted = true;
    api
      .get("/newsletter/unsubscribe", { params: { token } })
      .then((r) => {
        if (!mounted) return;
        setStatus("ok");
        setMessage(r.data?.message || "You are unsubscribed.");
      })
      .catch((e) => {
        if (!mounted) return;
        const detail = e?.response?.data?.detail;
        setStatus("error");
        setMessage(
          typeof detail === "string" ? detail : "Something went wrong."
        );
      });
    return () => {
      mounted = false;
    };
  }, [token]);

  return (
    <div data-testid="newsletter-unsubscribe-page">
      <PageHead
        title="Unsubscribe — Redkey Web Design"
        description="Unsubscribe from the Redkey Web Design blog list."
      />
      <section className="mx-auto max-w-[640px] px-6 md:px-10 py-24">
        <p className="eyebrow">§ Newsletter · Unsubscribe</p>

        {status === "loading" && (
          <p className="mt-8 font-body text-ink-soft">Processing…</p>
        )}

        {status !== "loading" && (
          <>
            <div className="mt-6 flex items-start gap-4">
              <div className="shrink-0 mt-2 h-10 w-10 rounded-full bg-paper-alt border hairline-strong flex items-center justify-center">
                <Mail size={20} aria-hidden />
              </div>
              <div>
                <h1
                  className="font-display text-4xl md:text-5xl leading-[1.02]"
                  data-testid={status === "ok" ? "newsletter-unsub-ok" : "newsletter-unsub-error"}
                >
                  You&rsquo;re <em className="not-italic text-brand-red">unsubscribed.</em>
                </h1>
                <p className="mt-4 font-body text-ink-soft max-w-prose leading-relaxed">
                  {message}
                </p>
                <p className="mt-3 font-body text-ink-soft max-w-prose leading-relaxed">
                  If this was a mistake, you can re-subscribe on the blog at any time.
                </p>
              </div>
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 bg-ink text-paper px-6 py-3 hover:bg-electric transition-colors font-ui"
              >
                Back to blog <span aria-hidden>→</span>
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 border hairline-strong px-6 py-3 hover:border-ink font-ui"
              >
                Home
              </Link>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
